"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { PageRulesSchema } from "@/sanity/pages/PageRulesTemplate";

import { PortableText } from "next-sanity";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";
import Footer from "@/components/global/partials/Footer";
import { cn } from "@/lib/utils";
import Chip from "../global/ui/Chip";
import Header from "../global/partials/Header";
import bg from "@/public/rules/bg.png";
import Image from "next/image";
import ScrollingBanner from "../pageComponents/global/ScrollingBanner";
import HeaderV2 from "../global/partials/HeaderV2";

export default function PageRulesJSX({ data }: { data: PageRulesSchema }) {
  const [selectedTitle, setSelectedTitle] = useState<string | null>(
    data?.rules?.[0]?.title ?? null,
  );
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayedTitle, setDisplayedTitle] = useState<string | null>(
    data?.rules?.[0]?.title ?? null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleSelectTitle = useCallback(
    (title: string) => {
      if (title === selectedTitle || isAnimating) return;

      setIsAnimating(true);
      setSelectedTitle(title);

      // Wait for fade-out, then update content and fade-in
      setTimeout(() => {
        setDisplayedTitle(title);
        setTimeout(() => {
          setIsAnimating(false);
        }, 50);
      }, 200);
    },
    [selectedTitle, isAnimating],
  );

  const selectedRule = data?.rules?.find(
    (rule) => rule.title === selectedTitle,
  );
  const filteredRules = displayedTitle
    ? data?.rules?.filter((rule) => rule.title === displayedTitle)
    : data?.rules;

  const isBrazil = data?.settings?.slug?.current?.endsWith("/br");

  useEffect(() => {
    const activeIndex = data?.rules?.findIndex(
      (rule) => rule.title === selectedTitle,
    );
    if (
      activeIndex !== undefined &&
      activeIndex >= 0 &&
      buttonRefs.current[activeIndex] &&
      containerRef.current
    ) {
      const button = buttonRefs.current[activeIndex];
      const container = containerRef.current;
      if (button && container) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        setUnderlineStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
        });
      }
    }
  }, [selectedTitle, data?.rules]);

  return (
    <main className="bg-[#FFF]">
      {/* Hero Section */}

      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType={"main"} // showBorder
          language={data.settings?.language}
        />
      )}
      <div className="flex relative flex-col justify-between transition-all duration-300 overflow-clip px-[2rem] pt-[12rem] lg:h-[60.375rem] lg:pt-[20rem]">
        <Image
          src={bg}
          alt=""
          className="object-cover absolute inset-0 w-full h-full z-[1]"
        />
        <div className="container flex relative flex-col items-center z-[5]">
          <Chip text={data?.heroChip} />

          {data?.heroText && (
            <div className="richtext mb-[5rem] lg:mb-[14rem] prose-p:w-[min(82rem,100%)]">
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        <div
          ref={containerRef}
          className={cn(
            "mx-auto flex justify-between relative z-[5]",
            isBrazil ? "lg:w-[min(93rem,100%)]" : "w-[min(91.5rem,100%)]",
          )}
        >
          {data?.rules &&
            data?.rules.map((item, index) => (
              <button
                key={index}
                ref={(el) => {
                  buttonRefs.current[index] = el;
                }}
                onClick={() => handleSelectTitle(item.title)}
                className={`cursor-pointer lg:px-[2rem] transition-opacity pb-[5.625rem] ${
                  selectedTitle === item.title
                    ? "opacity-100"
                    : "opacity-60 hover:opacity-80"
                }`}
              >
                <p className="relative text-[1.75rem] lg:text-[2.5rem] text-[#2E3436] text-center leading-[116%] tracking-[-0.56px] lg:tracking-[-.1rem] font-geist">
                  <span className="invisible font-semibold" aria-hidden="true">
                    {item.title}
                  </span>
                  <span
                    className={`absolute inset-0 ${selectedTitle === item.title ? "font-semibold" : "font-normal"}`}
                  >
                    {item.title}
                  </span>
                </p>
              </button>
            ))}
          <span
            className="absolute bottom-0 transition-all duration-300 ease-out translate-y-1/2 h-[1.25rem]"
            style={{
              left: underlineStyle.left,
              width: underlineStyle.width,
              backgroundColor: selectedRule?.mainColor || "#84ACC9",
            }}
          />
        </div>
      </div>

      {/* Rules Section */}
      <div className="w-[min(100rem,100%)] mx-auto pt-[12rem] lg:pt-[15rem]">
        {filteredRules && filteredRules.length > 0 && (
          <div className="container pb-[10rem] lg:pb-[20rem]">
            <div
              className={`w-[min(120rem,100%)] mx-auto flex flex-col gap-[4rem] lg:gap-[8rem] transition-all duration-200 ease-out ${
                isAnimating
                  ? "opacity-0 translate-y-4"
                  : "opacity-100 translate-y-0"
              }`}
            >
              {filteredRules.map((rule, index) => (
                <div key={index} className="flex flex-col gap-[2rem]">
                  {rule.content && (
                    <div className="richtext richtext-start prose-h2:text-[5rem] prose-h2:leading-[116%]">
                      <PortableText
                        value={rule.content}
                        components={customRichTextComponent({
                          block: {
                            h5: ({ children }) => {
                              const tableHeads = (
                                children as string[]
                              )?.[0]?.split("|");
                              return (
                                <div className="grid grid-cols-3 w-full">
                                  {(tableHeads ?? [])?.map((head, index) => (
                                    <div
                                      key={index}
                                      className={cn(
                                        "w-full flex items-center px-[1.5rem] lg:px-[2.5rem] py-[2rem] lg:py-[4rem] border border-black/10 ",
                                        index === 0 && "rounded-tl-[1.5rem]",
                                        index === 2 && "rounded-tr-[1.5rem]",
                                      )}
                                    >
                                      <span className="font-sf text-[#1C1F22]/90 text-[1.75rem] lg:text-[2.25rem] font-[600] leading-[150%]">
                                        {head}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              );
                            },
                            h6: ({ children }) => {
                              const tableCells = (
                                children as string[]
                              )?.[0]?.split("|");
                              return (
                                <div className="grid grid-cols-3 w-full">
                                  {(tableCells ?? [])?.map((cell, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center w-full border border-black/10 px-[1.5rem] py-[2rem] lg:px-[2.5rem] lg:py-[4rem]"
                                    >
                                      <span className="font-sf text-[#1C1F22] text-[1.75rem] lg:text-[2rem] font-[400] leading-[150%]">
                                        {cell}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              );
                            },
                          },
                        })}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ScrollingBanner
        className="bg-[#f4f5f5] border-y-0"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />
      <Footer data={data?.settings?.footer} finalCta={data} language={data?.settings?.language} />
    </main>
  );
}
